import { LoyaltyShoppingProvider } from '~/components/loyalty-shopping';
import { useEffect } from 'react';
import { json, type LoaderFunction } from '@remix-run/node';
import { Outlet, useMatches, useLoaderData } from '@remix-run/react';
import { getBrandLogoMap } from '~/api/brands-supabase.server';
import Header from '~/components/header';
import Footer from '~/components/footer';
// TabBar removed — fixed header makes bottom nav redundant (file kept for reference)
// import TabBar from '~/components/tab-bar';
import { CartProvider } from '~/context/cart-context';
import { ModalProvider } from '~/context/modal-context';
import { ReviewProvider } from '~/context/review-context';
import { BrandLogoProvider } from '~/context/brand-logo-context';
import SideCart from '~/components/side-cart';
import GlobalProductModal from '~/components/global-product-modal';
import { Toaster } from 'react-hot-toast';
import MagicLinkHandler from '~/components/magic-link-handler';

// SSR the brand-logo map (cached ~10 min) so the brand-logo context is seeded on
// first render — product cards show the brand-logo filler immediately instead of
// flashing the Catalyst default.webp then swapping to the brand logo.
export const loader: LoaderFunction = async () => {
  const brandLogos = await getBrandLogoMap();
  return json({ brandLogos });
};

export default function Layout() {
  const { brandLogos } = useLoaderData<{ brandLogos: Record<string, string> }>();
  const matches = useMatches();
  const isSocialRoute = matches.some((match) => match.id.includes('social'));
  const isCheckoutRoute = matches.some((match) => match.id.includes('checkout'));

  useEffect(() => {
    const handleStoreChange = () => {
      window.location.reload();
    };

    window.addEventListener('store:change', handleStoreChange);
    return () => {
      window.removeEventListener('store:change', handleStoreChange);
    };
  }, []);

  return (
    <CartProvider>
      <ModalProvider>
        <BrandLogoProvider initialLogos={brandLogos}>
          <ReviewProvider>
            <LoyaltyShoppingProvider>
              <MagicLinkHandler />
              <Toaster
                position="top-left"
                reverseOrder={false}
                containerStyle={{
                  zIndex: 20000,
                  maxWidth: 'calc(100vw - 32px)',
                  overflow: 'hidden',
                }}
              />
              <div className="app-container flex min-h-screen flex-col bg-white">
                {/* Single self-contained fixed element (no sr-only wrapper — its clip: rect()
                  clips the fixed link in Chrome, so it showed briefly then vanished). Hidden by
                  translating off-screen, revealed on focus, high z-index to clear page headers. */}
                <a
                  href="#main-content"
                  className="fixed top-2 left-4 z-[9999] -translate-y-[200%] rounded border bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-lg transition-transform focus:translate-y-0 focus:ring-2 focus:ring-[#ffd100] focus:outline-none"
                >
                  Skip to main content
                </a>
                {!isSocialRoute && !isCheckoutRoute && <Header />}
                <main id="main-content" className="main-content flex-1">
                  <Outlet />
                </main>
                {!isSocialRoute && !isCheckoutRoute && <Footer />}
                <SideCart />
                <GlobalProductModal />
              </div>
            </LoyaltyShoppingProvider>
          </ReviewProvider>
        </BrandLogoProvider>
      </ModalProvider>
    </CartProvider>
  );
}
